import React, { useEffect, useState, useRef } from "react";
import ChatSection from "./ChatSection/ChatSection";
import ParticipantsSection from "./ParticipantsSection/ParticipantsSection";
import VideoSection from "./VideoSection/VideoSection";
import { connect } from "react-redux";
import * as webRTCHandler from "../utils/webRTCHandler";
import Overlay from "./Overlay";
import VideoButtons from "./VideoSection/VideoButtons";
import LanguageDropdown from "./LanguageDropdown/LanguageDropdown";
import { FaUsers, FaComment, FaCopy, FaCheck } from "react-icons/fa";
import { setIsChatModalOpen, setIsParticipantsModalOpen } from "../store/actions";
import * as wss from "../utils/wss";
import { AudioTranslator } from "../utils/audioTranslation";

import "./RoomPage.css";

const RoomPage = ({
  roomId,
  identity,
  isRoomHost,
  showOverlay,
  connectOnlyWithAudio,
  participants,
  socketId,
  isChatOpen,
  isParticipantsOpen,
  setIsChatModalOpen,
  setIsParticipantsModalOpen,
}) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState([]);
  const [meetingDuration, setMeetingDuration] = useState(0);
  const [copied, setCopied] = useState(false);
  const startTimeRef = useRef(null);

  // Language translation state
  const [selectedLanguage, setSelectedLanguage] = useState('en'); // Default to English
  const [isTranslating, setIsTranslating] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState(null);
  const audioTranslatorRef = useRef(null);

  useEffect(() => {
    if (!isRoomHost && !roomId) {
      const siteUrl = window.location.origin;
      window.location.href = siteUrl;
    } else {
      webRTCHandler.getLocalPreviewAndInitRoomConnection(
        isRoomHost,
        identity,
        roomId,
        connectOnlyWithAudio
      );
      startTimeRef.current = Date.now();
    }

    const waitForStream = setInterval(() => {
      const stream = webRTCHandler.getLocalStream();
      const streams = webRTCHandler.getRemoteStreams();
      setRemoteStreams(streams || []);

      if (stream) {
        setLocalStream(stream);
        clearInterval(waitForStream);
      }
    }, 1);

    const durationInterval = setInterval(() => {
      if (startTimeRef.current) {
        const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setMeetingDuration(duration);
      }
    }, 1000);

    return () => {
      clearInterval(waitForStream);
      clearInterval(durationInterval);
    };
  }, [roomId, isRoomHost]);

  // Handle browser close/leave for host - notify server to end meeting
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRoomHost) {
        // Send synchronous request to notify server
        wss.hostLeaveMeeting(roomId, isRoomHost);
      }
    };

    const handleUnload = () => {
      if (isRoomHost) {
        wss.hostLeaveMeeting(roomId, isRoomHost);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [roomId, isRoomHost]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCloseChat = () => {
    setIsChatModalOpen(false);
  };

  const handleCloseParticipants = () => {
    setIsParticipantsModalOpen(false);
  };

  const copyMeetingLink = () => {
    const meetingLink = `${window.location.origin}/join-room?id=${roomId}`;
    navigator.clipboard.writeText(meetingLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Initialize audio translator
  useEffect(() => {
    audioTranslatorRef.current = new AudioTranslator({
      sourceLanguage: 'en',
      targetLanguage: selectedLanguage,
      onTranslation: (result) => {
        setCurrentTranslation(result);
      },
      onError: (error) => {
        console.error('Translation error:', error);
        setIsTranslating(false);
      },
      onStatusChange: (status) => {
        console.log('Translation status:', status);
      },
    });

    // Initialize the speech recognizer
    if (audioTranslatorRef.current) {
      const initialized = audioTranslatorRef.current.initialize();
      if (!initialized) {
        console.error('Failed to initialize speech recognizer. Please ensure microphone permissions are granted.');
      }
    }

    return () => {
      if (audioTranslatorRef.current) {
        audioTranslatorRef.current.stopTranslation();
      }
    };
  }, []);

  // Handle language change
  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    if (audioTranslatorRef.current) {
      audioTranslatorRef.current.setTargetLanguage(langCode);
    }
  };

  // Toggle translation
  const handleToggleTranslation = async () => {
    if (!audioTranslatorRef.current) {
      console.error('Audio translator not initialized');
      return;
    }

    if (isTranslating) {
      // Stop translation
      audioTranslatorRef.current.stopTranslation();
      setIsTranslating(false);
      setCurrentTranslation(null);
    } else {
      // Get remote stream if available
      const remoteStream = remoteStreams.length > 0 ? remoteStreams[0] : null;

      // Ensure source language is set to English (the language you'll speak)
      audioTranslatorRef.current.setSourceLanguage('en');

      // Set target language (the language to translate to)
      audioTranslatorRef.current.setTargetLanguage(selectedLanguage);

      console.log('Starting translation:');
      console.log('- You speak in: English (en)');
      console.log('- Translation will be in:', selectedLanguage);

      // Start translation - uses microphone for speech recognition
      audioTranslatorRef.current.startTranslation(remoteStream);
      setIsTranslating(true);
      setCurrentTranslation(null);
    }
  };

  return (
    <div className="room_container">
      {/* Top Room Label Bar */}
      <div className="room_label">
        <div className="room_label_left">
          <div className="room_label_paragraph">GooN Meeting</div>
          <div className="room_duration">
            ⏱ {formatDuration(meetingDuration)}
          </div>
        </div>

        <div className="room_label_right">
          {/* Language Translation Dropdown */}
          <LanguageDropdown
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
            isTranslating={isTranslating}
            onToggleTranslation={handleToggleTranslation}
            currentTranslation={currentTranslation}
          />

          {/* Copy Link Button */}
          <button
            className="copy_link_button"
            onClick={copyMeetingLink}
            title="Copy meeting link"
          >
            {copied ? <FaCheck /> : <FaCopy />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>

          {/* Participant Count */}
          <div
            className="participant_count_badge"
            onClick={() => setIsParticipantsModalOpen(true)}
          >
            <FaUsers />
            <span>{participants.length}</span>
          </div>
        </div>
      </div>

      {/* Main Video Section */}
      <VideoSection />

      {/* Video Controls */}
      <VideoButtons
        meetingStream={remoteStreams}
        roomId={roomId}
        isRoomHost={isRoomHost}
        showRecording={isRoomHost}
      />

      {/* Overlay */}
      {showOverlay && <Overlay />}

      {/* Chat Modal - Custom Popup */}
      <div className={`modal-custom ${isChatOpen ? 'show' : ''}`} onClick={handleCloseChat}>
        <div className="modal-dialog-custom" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">
              <FaComment /> Talk Zone
            </h5>
            <button className="modal-close-custom" onClick={handleCloseChat}>&times;</button>
          </div>
          <div className="modal-body-custom">
            <ChatSection />
          </div>
        </div>
      </div>

      {/* Participants Modal - Custom Popup */}
      <div className={`modal-custom ${isParticipantsOpen ? 'show' : ''}`} onClick={handleCloseParticipants}>
        <div className="modal-dialog-custom" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">
              <FaUsers /> Participants ({participants.length})
            </h5>
            <button className="modal-close-custom" onClick={handleCloseParticipants}>&times;</button>
          </div>
          <div className="modal-body-custom">
            <ParticipantsSection
              isRoomHost={isRoomHost}
              roomId={roomId}
              socketId={socketId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStoreStateToProps = (state) => {
  return {
    ...state,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setIsChatModalOpen: (isChatOpen) => dispatch(setIsChatModalOpen(isChatOpen)),
    setIsParticipantsModalOpen: (isParticipantsOpen) => dispatch(setIsParticipantsModalOpen(isParticipantsOpen)),
  };
};

export default connect(mapStoreStateToProps, mapDispatchToProps)(RoomPage);
